import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import { RootState } from "../../../app/store";
import { CommandObject } from "../../../redux/command/commandSlice";
import { widgetActions } from "../../../redux/widget/widgetSlice";
import { hour, displayDate, sameDay } from "../../../utils/TimeUtils";
import { NAVIGATION_GRID_ID, COMMAND_WIDGET_ID } from "../../../config/widget";
import { COMMAND_PENDING_STATUS, COMMAND_SUCCESS_STATUS, COMMAND_FAILURE_STATUS } from "../../../config/command";
import { RemoteDevice } from "./CnesCommandTab";

const STANDARD_RELAY_WIDTH = 410;

const status2color = (status: number) => (
    status === -1 ? // unsupported
        "red" // for now, any unsupported state is to be considered an error
    : status === 0 ? // pending
        "grey"
    : status === 1 ? // success
        "limeGreen"
    : status === 2 ? // failure
        "red"
    : "white" // none (should never occur)
);

const RelaySchematic = ({ active, pending, status, width }: { active: number, pending: number, status: number, width?: number }) => {
    const ratio = width ? (width + (STANDARD_RELAY_WIDTH - width)/4) / STANDARD_RELAY_WIDTH : 1;
    // ^ linear ratio with a slope of 1/4
    const xStart = -30 * ratio;
    const xText = 80 * ratio;
    const xCom = 100 * ratio;
    const xEnd = 230 * ratio;
        return (
        // active: -1 -> unknown, 0 -> Repos, 1 -> Actif
        // pending: -1 -> none, 0 -> Repos, 1 -> Actif
        // status: -1 -> unknown or N.A., 0 -> pending, 1 -> success, 2 -> failure
        <svg width={width ? width - 50 : "100%"} height="120" viewBox="0 0 300 120">
            {/* Line from coil to COM */}
            <line x1={xStart} y1="60" x2={xCom - 10} y2="60" stroke="white" strokeWidth="2" />

            {/* Repos contact */}
            {/*<text x="240" y="15" fontSize="20" fontWeight={isOn ? "normal" : "bold"} fill="white">Repos</text>*/}
            <line x1={xCom} y1="60" x2={xEnd} y2="10" stroke={status2color(status)} strokeWidth={active === pending && pending !== -1 ? "6" : "2"} opacity={pending === 0 ? 1 : 0} />
            <line x1={xCom} y1="60" x2={xEnd} y2="10" stroke="white" strokeWidth="3" opacity={active === 0 ? 1 : 0} />
            <circle cx={xEnd} cy="10" r="4" fill="white" />

            {/* Actif contact */}
            {/*<text x="240" y="115" fontSize="20" fontWeight={isOn ? "bold" : "normal"} fill="white">Actif</text>*/}
            <line x1={xCom} y1="60" x2={xEnd} y2="110" stroke={status2color(status)} strokeWidth={active === pending && pending !== -1 ? "6" : "2"} opacity={pending === 1 ? 1 : 0} />
            <line x1={xCom} y1="60" x2={xEnd} y2="110" stroke="white" strokeWidth="3" opacity={active === 1 ? 1 : 0} />
            <circle cx={xEnd} cy="110" r="4" fill="white" />

            {/* COM terminal */}
            <circle cx={xCom} cy="60" r="4" fill="white" />
            <text x={xText} y="45" fontSize="18" fill="white">COM</text>

            {/* State indicator */}
            {/*<text x="260" y="65" fontSize="22" fontWeight="bold" fill={isOn ? '#4caf50' : '#f44336'}>{isOn ? 'ON' : 'OFF'}</text>*/}
        </svg>
    );
};

const BuzzerSchematic = ({ state, pending, status, buzzerOn, buzzerOff, disabled }: { state: number, pending: number, status: number, buzzerOn: () => void, buzzerOff: () => void, disabled: boolean }) => {
    return (
        state !== pending && pending !== -1 ? // TODO: account for pending frequency different from current frequency if frequency can be changed by user
            <>
                {state ?
                    <Stack direction="row" spacing={8} sx={{position: "absolute", right: -85, top: -30}}>
                        <Tooltip title="Turn buzzer on" placement="left">
                            <IconButton onClick={buzzerOn} size="large" disabled={disabled}>
                                <NotificationsActiveIcon fontSize="large"/>
                            </IconButton>
                        </Tooltip>
                        <ArrowForwardIcon fontSize="large" sx={{position: "absolute", right: 72, top: 15, color: status2color(status)}}/>
                        <Tooltip title="Turn buzzer off" placement="right">
                            <IconButton onClick={buzzerOff} size="large" disabled={disabled}>
                                <NotificationsOffIcon fontSize="large"/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                :
                    <Stack direction="row" spacing={8} sx={{position: "absolute", right: -85, top: -30}}>
                        <Tooltip title="Turn buzzer off" placement="left">
                            <IconButton onClick={buzzerOff} size="large" disabled={disabled}>
                                <NotificationsOffIcon fontSize="large"/>
                            </IconButton>
                        </Tooltip>
                        <ArrowForwardIcon fontSize="large" sx={{position: "absolute", right: 72, top: 15, color: status2color(status)}}/>
                        <Tooltip title="Turn buzzer on" placement="right">
                            <IconButton onClick={buzzerOn} size="large" disabled={disabled}>
                                <NotificationsActiveIcon fontSize="large"/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                }
            </>
        : state ?
            <>
                <Tooltip title="Turn buzzer off" placement="top">
                    <IconButton onClick={buzzerOff} size="large" sx={{position: "absolute", right: -25, top: -30}} disabled={disabled}>
                        <NotificationsActiveIcon fontSize="large"/>
                    </IconButton>
                </Tooltip>
                <StatusIcon status={status} sx={{position: "absolute", right: -26, top: -32}}/>
            </>
        :
            <>
                <Tooltip title="Turn buzzer on" placement="top">
                    <IconButton onClick={buzzerOn} size="large" sx={{position: "absolute", right: -25, top: -30}} disabled={disabled}>
                        <NotificationsOffIcon fontSize="large"/>
                    </IconButton>
                </Tooltip>
                <StatusIcon status={status} sx={{position: "absolute", right: -25, top: -30}}/>
            </>
    );
};

const StatusIcon = ({status, sx}: {status: number, sx?: any}) => (
    status === -1 ? // unknown
        <></>
    : status === 0 ? // pending
        <AutorenewIcon fontSize="medium" sx={{color: "grey", ...sx}} />
    : status === 1 ? // success
        <DoneIcon fontSize="medium" sx={{color: "limegreen", ...sx}}/>
    : status === 2 ? // failure
        <CloseIcon fontSize="medium" sx={{color: "red", ...sx}}/>
    : null // unknown
);

const status2code = (status: string) => (
    COMMAND_PENDING_STATUS.includes(status) ?
        0
    : COMMAND_SUCCESS_STATUS.includes(status) ?
        1
    : COMMAND_FAILURE_STATUS.includes(status) ?
        2
    : status === undefined ?
        -1
    : 2 // unknown statuses are considered as errors for now
);

export const Device = ({ kind, device, deviceCommand, commandHistory, width, isWidget }: { kind: string, device: RemoteDevice, deviceCommand: (destination: string, relayId: string, state: number) => void, commandHistory: CommandObject[], width?: number, isWidget?: boolean }) => {

    const replayIsOn = useSelector((state: RootState) => state.replay.isActive);

    const selfHistory = commandHistory.filter(command => command.parameters?.id ? command.parameters?.id === device.id : command.type === kind).reverse();
    const lastCommand = selfHistory[0];

    const now = Math.round(Date.now() / 1000);

    const [expanded, setExpanded] = useState<boolean>(false);

    const status = status2code(lastCommand?.status);

    const expandHistory = () => {
        setExpanded(!expanded);
    };

    const buzzerOn = () => deviceCommand(device.source, device.id, 1000);

    const buzzerOff = () => deviceCommand(device.source, device.id, 0);
    
    return (<Box sx={{display: "flex", flexDirection: "column", height: "100%"}}>
        {kind === "relay" ?
            <Box sx={{my: 2, position: "relative", flexGrow: 1}}>
                <RelaySchematic active={device.state} pending={device.pending} status={status} width={width}/>
                <Button
                    variant="outlined"
                    color="inherit"
                    disabled={replayIsOn}
                    sx={{position: "absolute", right: 15, top: -8, width: 15}}
                    onClick={() => deviceCommand(device.source, device.id, 0)}
                >
                    Repos
                </Button>
                <Button
                    variant="outlined"
                    color="inherit"
                    disabled={replayIsOn}
                    sx={{position: "absolute", right: 15, top: 92, width: 15}}
                    onClick={() => deviceCommand(device.source, device.id, 1)}
                >
                    Actif
                </Button>
            </Box>
        : kind === "buzzer" ?
            <Box sx={{my: 2, display: "flex", flexGrow: 1, justifyContent: "center", alignItems: "center", ...(isWidget ? {} : {height: 126})}}>
                <Box sx={{position: "relative"}}>
                    <BuzzerSchematic state={device.state} pending={device.pending} status={status} buzzerOn={buzzerOn} buzzerOff={buzzerOff} disabled={replayIsOn}/>
                </Box>
                {/* TODO: add frequency setter ? */}
            </Box>
        : "missing schematic..."}

        <Stack direction="column" spacing={1} marginBottom={-2}>
            <Stack direction="row" spacing={2} marginBottom={-2}>
                <Typography variant="h6">Last Command:</Typography>
                <Tooltip title={`${expanded ? "hide" : "show"} command history`} placement="right">
                    <IconButton onClick={expandHistory} size="small">
                        {expanded ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                    </IconButton>
                </Tooltip>
            </Stack>
            <Stack direction="row" spacing={3.5}>
                <Stack direction="column" spacing={0}>
                    <Typography variant="body1">Target:</Typography>
                    <Typography variant="body1">{
                        lastCommand?.parameters?.state !== undefined ?
                            kind === "relay" ?
                                lastCommand.parameters.state ? "Actif" : "Repos"
                            : kind === "buzzer" ?
                                lastCommand.parameters.state ? "ON" : "OFF"
                            : "-"
                        : "-"
                    }</Typography>
                </Stack>
                <Stack direction="column" spacing={0}>
                    <Typography variant="body1">Status:</Typography>
                    <Typography variant="body1">{lastCommand?.status || "-"}</Typography>
                </Stack>
                <Stack direction="column" spacing={0}>
                    <Typography variant="body1">Sent:</Typography>
                    <Typography variant="body1">{lastCommand?.created ?
                        sameDay(now, lastCommand.created) ?
                            hour(lastCommand.created)
                        :
                            `${displayDate(lastCommand.created)}\n${hour(lastCommand.created)}`
                    :
                        "-"
                    }</Typography>
                </Stack>
                <Stack direction="column" spacing={0}>
                    <Typography variant="body1">Last Update:</Typography>
                    <Typography variant="body1">{lastCommand?.updated ?
                        sameDay(now, lastCommand.updated) ?
                            hour(lastCommand.updated)
                        :
                            `${displayDate(lastCommand.updated)}\n${hour(lastCommand.updated)}`
                    :
                        "-"
                    }</Typography>
                </Stack>
            </Stack>
        </Stack>

        {expanded &&
            <TableContainer sx={{marginTop: 3, maxHeight: 268, marginBottom: isWidget ? -4 : 0 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow color="secondary" sx={{ height: '50px', backgroundColor: (theme) => theme.palette.primary.light }}>
                            <TableCell sx={{ height: '50px', backgroundColor: (theme) => theme.palette.primary.main }}>Target</TableCell>
                            <TableCell sx={{ height: '50px', backgroundColor: (theme) => theme.palette.primary.main }}>Status</TableCell>
                            <TableCell sx={{ height: '50px', backgroundColor: (theme) => theme.palette.primary.main }}>Created</TableCell>
                            <TableCell sx={{ height: '50px', backgroundColor: (theme) => theme.palette.primary.main }}>Last Updated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selfHistory.map((command, index) => (
                            <TableRow key={index} sx={{ backgroundColor: (theme) => theme.palette.primary.dark}}>
                                <TableCell>{
                                    command.parameters?.state !== undefined ?
                                        kind === "relay" ?
                                            command.parameters.state ? "Actif" : "Repos"
                                        : kind === "buzzer" ?
                                            command.parameters.state ? "ON" : "OFF"
                                        : "-"
                                    : "-"
                                }</TableCell>
                                <TableCell>{command.status}</TableCell>
                                <TableCell>{command.created ? 
                                    sameDay(now, command.created) ?
                                        hour(command.created)
                                    :
                                        `${displayDate(command.created)}\n${hour(command.created)}`
                                :
                                    "-"
                                }</TableCell>
                                <TableCell>{command.updated ?
                                    sameDay(now, command.updated) ?
                                        hour(command.updated)
                                    :
                                        `${displayDate(command.updated)}\n${hour(command.updated)}`
                                :
                                    "-"
                                }</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        }
    </Box>);
};

const DeviceCard = ({ kind, device, deviceCommand, commandHistory, source, label, name }: { kind: string, device: RemoteDevice, deviceCommand: (destination: string, relayId: string, state: number) => void, commandHistory: CommandObject[], source: string, label: string, name: string }) => {

    const medium = useMediaQuery((theme: any) => theme.breakpoints.down('xl'));
    const dispatch = useDispatch();

    const addWidget = () => {
        dispatch(widgetActions.add_widget({ gridId: NAVIGATION_GRID_ID, id: uuidv4(), kind: COMMAND_WIDGET_ID, params: { kind, device, source, label, name }, medium }));
    };

    return (
        <Card sx={{ minWidth: 395, m: 2, minHeight: 330, maxHeight: 600, overflow: "scroll"}}>
            <CardContent sx={{overflow: "scroll", marginTop: -1}}>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>{device.name}</Typography>
                    <Tooltip title="Add Widget" placement="top">
                        <IconButton onClick={addWidget} sx={{marginRight: -1}}>
                            <WebAssetIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Device kind={kind} device={device} deviceCommand={deviceCommand} commandHistory={commandHistory}/>
            </CardContent>
        </Card>
    );
};

export default DeviceCard;